import { useState, useCallback, useRef, useEffect } from "react";
import { parseUsername } from "../utils/api.js";
import { buildPeriodAnalytics, DEFAULT_PERIOD_DAYS, PERIOD_OPTIONS } from "../utils/analytics.js";
import { loadProfile, computePeriodWarning } from "../utils/loadProfile.js";
import { parseLocation, buildPath, currentPath, buildTitle, MAX_COMPARE_PROFILES } from "../utils/url.js";

const STORAGE_KEY = "hevy_username";

const INITIAL = {
  screen: "search",
  username: "",
  preview: null,
  history: null,
  workouts: [],
  periodDays: DEFAULT_PERIOD_DAYS,
  weightUnit: "kg",
  analytics: null,
  status: "idle",
  dataStatus: "idle",
  error: null,
  historyWarning: null,
  periodWarning: null,
  historyProgress: null,
  searchKey: 0,
  // { profiles: [{ username, status, error, progress, ...loadProfile result }] }
  compare: null
};

export function sameName(a, b) {
  return String(a || "").toLowerCase() === String(b || "").toLowerCase();
}

export function compareProfiles(state) {
  return state.compare?.profiles || [];
}

// A profile whose import failed is not part of the comparison — its message
// stands only until the next one is added, and then it goes.
export function liveCompareProfiles(state) {
  return compareProfiles(state).filter((p) => p.status !== "error");
}

// The usernames a comparison URL may name: only the ones actually on screen, so
// a profile that failed to import never ends up in a link someone shares.
export function readyCompareNames(state) {
  return compareProfiles(state).filter((p) => p.status === "ready").map((p) => p.username);
}

// Per-profile updates land by username; if the profile was removed while its
// import was still running, the patch is a no-op instead of resurrecting it.
export function patchCompareProfile(state, username, patch) {
  const profiles = compareProfiles(state);
  if (!profiles.some((p) => sameName(p.username, username))) return state;
  return {
    ...state,
    compare: {
      ...state.compare,
      profiles: profiles.map((p) => (sameName(p.username, username) ? { ...p, ...patch } : p))
    }
  };
}

export function useWorkoutData() {
  const [state, setState] = useState(() => {
    const target = parseLocation();
    return {
      ...INITIAL,
      // A profile URL goes straight to loading, so a shared link never flashes
      // the search screen before the dashboard it actually points at.
      screen: target.username ? "loading" : "search",
      status: target.username ? "loading-preview" : "idle",
      dataStatus: target.username ? "loading" : "idle",
      username: target.username || localStorage.getItem(STORAGE_KEY) || "",
      periodDays: target.periodDays
    };
  });
  const searchIdRef = useRef(0);
  const compareIdRef = useRef(0);
  const stateRef = useRef(state);
  const identityRef = useRef(null);
  const applyUrlRef = useRef(null);

  stateRef.current = state;

  const search = useCallback(async (rawInput, { periodDays } = {}) => {
    const username = parseUsername(rawInput);
    if (!username) return false;

    const days = PERIOD_OPTIONS.includes(Number(periodDays)) ? Number(periodDays) : state.periodDays;
    const searchId = ++searchIdRef.current;
    // A comparison still importing belongs to the profile being left behind.
    compareIdRef.current++;

    setState((prev) => ({
      ...prev,
      screen: "loading",
      username,
      periodDays: days,
      preview: null,
      history: null,
      workouts: [],
      analytics: null,
      status: "loading-preview",
      dataStatus: "loading",
      error: null,
      historyWarning: null,
      periodWarning: null,
      historyProgress: null,
      compare: null
    }));

    let result;
    try {
      result = await loadProfile(username, days, {
        onPreview: (preview) => {
          if (searchId !== searchIdRef.current) return;
          setState((prev) => ({ ...prev, preview, status: "loading-history" }));
        },
        onProgress: (progress) => {
          if (searchId !== searchIdRef.current) return;
          setState((prev) => ({ ...prev, historyProgress: progress }));
        }
      });
    } catch (err) {
      if (searchId !== searchIdRef.current) return false;
      setState((prev) => ({ ...prev, screen: "search", status: "error", dataStatus: "error", error: err.message }));
      return false;
    }

    if (searchId !== searchIdRef.current) return false;

    // A private profile resolves with no workouts, so a dashboard built from it
    // would be all zeros and empty states. Stop on the search screen instead.
    if (result.dataStatus === "private") {
      setState((prev) => ({
        ...prev,
        screen: "search",
        status: "error",
        dataStatus: "error",
        error: "This profile is private. Workout history cannot be imported."
      }));
      return false;
    }

    setState((prev) => ({
      ...prev,
      screen: "dashboard",
      preview: result.preview,
      history: result.history,
      workouts: result.workouts,
      weightUnit: result.weightUnit,
      analytics: result.analytics,
      status: "ready",
      dataStatus: result.dataStatus,
      historyWarning: result.historyWarning,
      periodWarning: result.periodWarning,
      historyProgress: null
    }));

    localStorage.setItem(STORAGE_KEY, username);
    return true;
  }, [state.periodDays]);

  const setPeriod = useCallback((days) => {
    setState((prev) => {
      const analytics = buildPeriodAnalytics(prev.workouts, days);
      const periodWarning = computePeriodWarning(prev.workouts, prev.history?.pagination, days);

      const compare = prev.compare && {
        ...prev.compare,
        profiles: prev.compare.profiles.map((p) => (p.status !== "ready" ? p : {
          ...p,
          analytics: buildPeriodAnalytics(p.workouts, days),
          periodWarning: computePeriodWarning(p.workouts, p.history?.pagination, days)
        }))
      };

      return { ...prev, periodDays: days, analytics, periodWarning, compare };
    });
  }, []);

  // Adds one more profile to the comparison. Callers await it before adding the
  // next: every profile is a queued history import on the server, and the
  // per-IP queue cap is small — a four-profile link must not fire four at once.
  const startComparison = useCallback(async (rawInput, { periodDays, primaryUsername } = {}) => {
    const username = parseUsername(rawInput);
    if (!username) return false;

    const days = PERIOD_OPTIONS.includes(Number(periodDays)) ? Number(periodDays) : state.periodDays;
    const primary = primaryUsername || state.username;
    const already = liveCompareProfiles(stateRef.current);
    if (already.length >= MAX_COMPARE_PROFILES) return false;
    if (sameName(primary, username) || already.some((p) => sameName(p.username, username))) return false;

    // The base profile, the ones already on screen, and this one: the server
    // sees a comparison only as separate loads, so it cannot count them itself.
    const compareSize = already.length + 2;
    const compareId = compareIdRef.current;

    setState((prev) => {
      // Checked again against the state React actually holds: the read above
      // can be one render behind when two profiles are added back to back.
      const profiles = liveCompareProfiles(prev);
      if (profiles.length >= MAX_COMPARE_PROFILES) return prev;
      if (profiles.some((p) => sameName(p.username, username))) return prev;
      return {
        ...prev,
        compare: { ...prev.compare, profiles: [...profiles, { username, status: "loading", error: null, progress: null }] }
      };
    });

    let result;
    try {
      result = await loadProfile(username, days, {
        compareWith: primary,
        compareSize,
        onProgress: (progress) => {
          if (compareId !== compareIdRef.current) return;
          setState((prev) => patchCompareProfile(prev, username, { progress }));
        }
      });
    } catch (err) {
      if (compareId !== compareIdRef.current) return false;
      setState((prev) => patchCompareProfile(prev, username, { status: "error", error: err.message, progress: null }));
      return false;
    }

    if (compareId !== compareIdRef.current) return false;

    // A private profile still resolves, but with no workouts — comparing would
    // render it as a legitimate zero on every metric instead of missing data.
    if (result.dataStatus === "private") {
      setState((prev) => patchCompareProfile(prev, username, {
        status: "error",
        error: "This profile is private. There is no workout history to compare.",
        progress: null
      }));
      return false;
    }

    setState((prev) => {
      const next = patchCompareProfile(prev, username, { ...result, status: "ready", error: null, progress: null });
      return next === prev ? prev : { ...next, screen: "comparison" };
    });
    return true;
  }, [state.periodDays, state.username]);

  const removeComparison = useCallback((username) => {
    setState((prev) => {
      const profiles = compareProfiles(prev).filter((p) => !sameName(p.username, username));
      if (profiles.length === compareProfiles(prev).length) return prev;
      if (!profiles.length) return { ...prev, screen: "dashboard", compare: null };
      return { ...prev, compare: { ...prev.compare, profiles } };
    });
  }, []);

  const exitComparison = useCallback(() => {
    compareIdRef.current++;
    setState((prev) => ({ ...prev, screen: "dashboard", compare: null }));
  }, []);

  const reset = useCallback(() => {
    searchIdRef.current++;
    compareIdRef.current++;
    setState((prev) => ({
      ...INITIAL,
      username: prev.username,
      searchKey: prev.searchKey + 1
    }));
  }, []);

  // Restores whatever a URL names: a profile, a comparison, or the search screen.
  const applyUrl = useCallback(async (target) => {
    if (!target.username) {
      reset();
      return;
    }
    const loaded = await search(target.username, { periodDays: target.periodDays });
    if (!loaded) return;
    for (const name of target.compareWith) {
      await startComparison(name, {
        periodDays: target.periodDays,
        primaryUsername: target.username
      });
    }
  }, [search, startComparison, reset]);

  applyUrlRef.current = applyUrl;

  // Hydrate once from the URL the page was opened with.
  useEffect(() => {
    const target = parseLocation();
    identityRef.current = `${target.screen}:${target.username}:${target.compareWith.join(",")}`;
    if (target.username) applyUrlRef.current(target);
  }, []);

  // Push the URL from state, never the other way round: whatever the app is
  // showing is what the address bar says, so refresh and share both work.
  useEffect(() => {
    if (state.screen === "loading") return;

    const compareWith = readyCompareNames(state);
    const target = buildPath({
      screen: state.screen,
      username: state.username,
      compareWith,
      periodDays: state.periodDays
    });
    if (target === currentPath()) return;

    // Changing profile is navigation; changing the period filter is not, so it
    // replaces rather than filling the back stack.
    const identity = `${state.screen}:${state.username}:${compareWith.join(",")}`;
    if (identity === identityRef.current) window.history.replaceState(null, "", target);
    else window.history.pushState(null, "", target);
    identityRef.current = identity;
  }, [state.screen, state.username, state.compare, state.periodDays]);

  // Back and forward re-enter the app rather than leaving it.
  useEffect(() => {
    function onPopState() {
      const target = parseLocation();
      const now = stateRef.current;
      const compareWith = readyCompareNames(now);
      identityRef.current = `${target.screen}:${target.username}:${target.compareWith.join(",")}`;

      if (target.username && sameName(target.username, now.username)) {
        // Going back over an added profile, or out of the comparison entirely:
        // everything the URL still names is already imported, so drop the tail
        // rather than re-importing the profiles that stayed.
        const dropped = compareWith.slice(target.compareWith.length);
        const keptInOrder = target.compareWith.length <= compareWith.length
          && target.compareWith.every((name, i) => sameName(name, compareWith[i]));
        if (keptInOrder) {
          for (const name of dropped) removeComparison(name);
          if (target.periodDays !== now.periodDays) setPeriod(target.periodDays);
          return;
        }
      }
      applyUrlRef.current(target);
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [setPeriod, removeComparison]);

  // The server titles the HTML it serves; in-app navigation has to keep up or the
  // tab keeps naming whichever profile the page was opened on.
  useEffect(() => {
    document.title = buildTitle({
      username: state.screen === "search" ? "" : state.username,
      compareWith: state.screen === "comparison" ? readyCompareNames(state) : []
    });
  }, [state.screen, state.username, state.compare]);

  useEffect(() => {
    window.__HEVY_ANALYZER_STATE__ = {
      dataStatus: state.dataStatus,
      weightUnit: state.weightUnit,
      analytics: state.analytics
    };
  }, [state.dataStatus, state.weightUnit, state.analytics]);

  return { state, search, setPeriod, startComparison, removeComparison, exitComparison, reset };
}
